import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import IconContainer from '../icon/IconContainer'
import { RoundedCard } from './GenericCard'
import Text, { MarkDownText } from '../Text'
import JobTag from '../tag/JobTag'
import Fold from '../Fold'

// Types
import { ICompanyJobLevel } from '../../common/models/Company'
import { CompetencyType } from '../../common/models/Feedback'

// Utils
import { useAppState } from '../../context/AppContext'

interface IProps {
  competencyType: CompetencyType
  competencyName: string
  subtitle?: string
  description: string
  summary?: string
  levels?: ICompanyJobLevel[]
  descriptionFoldable?: boolean
  children?: React.ReactNode
}

const CompetencyCard: React.FC<IProps> = ({
  competencyType,
  competencyName,
  subtitle,
  description,
  summary,
  levels,
  descriptionFoldable,
  children,
}) => {
  const { theme } = useAppState()
  const { t } = useTranslation()

  return (
    <RoundedCard>
      <Header>
        <IconContainer
          padding="12px"
          size={24}
          color={theme.colors.text72}
          type={competencyType === CompetencyType.skill ? 'Skills' : 'Culture'}
        />
        <div>
          <Text textStyle="subtitle" color="primary">
            {subtitle}
          </Text>
          <Text textStyle="title">{competencyName}</Text>
          {levels && (
            <Levels>
              {levels.map((level) => (
                <JobTag content={level.title} key={level._id} />
              ))}
            </Levels>
          )}
        </div>
      </Header>
      {summary && (
        <Text textStyle="bodySmall" marginTop={16}>
          {summary}
        </Text>
      )}
      {descriptionFoldable ? (
        <StyledFold
          caption={t('valueCard.description')}
          content={
            <DescriptionContainer>
              <MarkDownText textStyle="bodySmall">{description}</MarkDownText>
            </DescriptionContainer>
          }
        />
      ) : (
        <>
          <Text
            textStyle="labelSmallCaps"
            color="text72"
            marginTop={summary ? 16 : 24}
            marginBottom={12}
          >
            {t('valueCard.description')}
          </Text>
          <DescriptionContainer>
            <MarkDownText textStyle="bodySmall">{description}</MarkDownText>
          </DescriptionContainer>
        </>
      )}
      {children}
    </RoundedCard>
  )
}

const Levels = styled.div`
  margin-top: 4px;
`

export const Header = styled.div`
  display: flex;
  align-items: center;
`

const DescriptionContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.primaryLight};
  border-radius: 12px;
  padding: 16px;
`

const StyledFold = styled(Fold)`
  margin-top: 16px;

  ${DescriptionContainer} {
    margin-bottom: 8px;
  }
`

export default CompetencyCard
